import {
  UserProfile,
  UserNutritionalNeeds,
  MacrosDistribution,
  RecipeTag,
  FoodRestriction,
  Meal,
  MealOfDayDivision,
  BaseUserProfile,
} from '@/api/types'
import { Grid } from '@/components/Grid'
import React from 'react'
import { Label } from 'react-aria-components'
import EnergyDistributionComponent from '@/features/meal-recommendations/EnergyDistributionComponent/EnergyDistributionComponent'
import FoodPreferences from '@/features/meal-recommendations/FoodPreferences/FoodPreferences'
import HouseholdSelector from '@/features/meal-recommendations/HouseholdSelector/HouseholdSelector'
import NutritionalNeeds from '@/features/meal-recommendations/NutritionalNeeds/NutritionalNeeds'
import ProfileEditor from '@/features/meal-recommendations/ProfileEditor'
import MealPreferencesControl from '@/features/MealPreferencesControl/MealPreferencesControl'

function SectionTitle({ children }) {
  return <h4 style={{ borderBottom: '1px solid var(--gray-100)', paddingBottom: 4 }}>{children}</h4>
}

type OnMealPreferencesChangeFn = (
  selectedMeals: Meal[],
  mealOfDayDivisions: MealOfDayDivision[],
) => void

type UpdateHouseholdMembersFn = (members: BaseUserProfile[]) => void

type UserProfilePanelProps = {
  userProfile: UserProfile
  nutritionalNeeds: UserNutritionalNeeds | undefined
  energyDistribution: MacrosDistribution
  tags: RecipeTag[]
  allergens: FoodRestriction[]
  isReadonly?: boolean
  onChangeProfile?: (profile: UserProfile) => void
  onChangeMealPreferences?: OnMealPreferencesChangeFn
  onChangeHouseholdMembers?: UpdateHouseholdMembersFn
}

export function UserProfilePanel({
  isReadonly = false,
  userProfile,
  nutritionalNeeds,
  energyDistribution,
  tags,
  allergens,
  onChangeProfile,
  onChangeMealPreferences: onMealPreferencesChange,
  onChangeHouseholdMembers: updateHouseholdMembers,
}: UserProfilePanelProps) {
  return (
    <Grid
      columns="4fr 1fr"
      style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <SectionTitle>User Profile</SectionTitle>
        <ProfileEditor
          isReadOnly={isReadonly}
          userProfile={userProfile}
          onChangeProfile={onChangeProfile}
        />

        <HouseholdSelector
          isReadonly={isReadonly}
          householdMembers={userProfile.household.users || []}
          onChange={updateHouseholdMembers}
        />
        <SectionTitle>Meals configuration</SectionTitle>
        <div>
          <Label>Macro distribution</Label>
          <EnergyDistributionComponent
            energyDistribution={{
              ...energyDistribution,
              ...userProfile.userEnergyDistribution,
            }}
            onChange={(userEnergyDistribution) =>
              !isReadonly && onChangeProfile?.({ ...userProfile, userEnergyDistribution })
            }
          />
        </div>
        <Label>Meal divisions</Label>
        <MealPreferencesControl
          selectedMeals={userProfile.household.selectedMeals || []}
          mealOfDayDivisions={userProfile.mealOfDayDivisions}
          isReadonly={isReadonly}
          onChange={onMealPreferencesChange}
        />

        <SectionTitle>Food Preferences</SectionTitle>
        <FoodPreferences
          tags={tags}
          allergens={allergens}
          selectedAllergens={userProfile.foodRestrictions}
          selectedDiets={userProfile.dietaryRestrictions}
          onChangeAllergens={(allergens) =>
            !isReadonly && onChangeProfile?.({ ...userProfile, foodRestrictions: allergens })
          }
          onChangeDiets={(diets) =>
            !isReadonly && onChangeProfile?.({ ...userProfile, dietaryRestrictions: diets })
          }
          onChangeFavoriteIngredients={(ingredients) =>
            !isReadonly &&
            onChangeProfile?.({ ...userProfile, userIngredientFeedbacks: ingredients })
          }
          onChangeFavoriteRecipes={(recipes) =>
            !isReadonly && onChangeProfile?.({ ...userProfile, userRecipeFeedbacks: recipes })
          }
          onChangeRestrictIngredients={(ingredientRestrictions) =>
            !isReadonly && onChangeProfile?.({ ...userProfile, ingredientRestrictions })
          }
        />
      </div>
      <NutritionalNeeds
        nutritionalNeeds={nutritionalNeeds}
        selectedMeals={userProfile.household.selectedMeals || []}
      />
    </Grid>
  )
}
